<template>
  <div v-if="!loading" class="sticky h-[calc(100vh-50px)] overflow-y-auto" data-testid="pages-seo-settings-drawer">
    <form data-testid="basic-settings-form" class="w-full shadow-[inset_0px_0px_20px_-20px_#111] absolute bg-white">
      <EditorFormPanel v-model="metaData" title="Meta Data" data-testid="open-basic-settings">
        <div class="py-2">
          <div class="flex justify-between mb-2">
            <UiFormLabel>Title</UiFormLabel>
            <SfTooltip :label="titleTooltip" :placement="'top'" :show-arrow="true" class="ml-2 z-dropdown">
              <SfIconInfo :size="'sm'" />
            </SfTooltip>
          </div>

          <label>
            <SfInput v-model="metaTitle" type="text" data-testid="seo-title" placeholder="Enter title">
              <template #suffix>
                <label for="page-id" class="rounded-lg cursor-pointer">
                  <input id="page-id" v-model="metaTitle" type="text" class="invisible w-8" />
                </label>
              </template>
            </SfInput>
          </label>
        </div>

        <div class="py-2">
          <div class="flex justify-between mb-2">
            <UiFormLabel>Meta description</UiFormLabel>
            <SfTooltip :label="descTooltip" :placement="'top'" :show-arrow="true" class="ml-2 z-dropdown">
              <SfIconInfo :size="'sm'" />
            </SfTooltip>
          </div>
          <label>
            <SfInput
              v-model="metaDescription"
              type="text"
              data-testid="seo-description"
              placeholder="Enter description"
            >
              <template #suffix>
                <label for="page-type" class="rounded-lg cursor-pointer">
                  <input id="page-type" v-model="metaDescription" type="text" class="invisible w-8" />
                </label>
              </template>
            </SfInput>
          </label>
        </div>

        <div class="py-2">
          <div class="flex justify-between mb-2">
            <UiFormLabel>Meta keywords</UiFormLabel>
            <SfTooltip :label="keywordsTooltip" :placement="'top'" :show-arrow="true" class="ml-2 z-dropdown">
              <SfIconInfo :size="'sm'" />
            </SfTooltip>
          </div>
          <label>
            <SfInput v-model="metaKeywords" type="text" data-testid="page-name" placeholder="Enter keywords">
              <template #suffix>
                <label for="page-name" class="rounded-lg cursor-pointer">
                  <input id="page-name" v-model="metaKeywords" type="text" class="invisible w-8" />
                </label>
              </template>
            </SfInput>
          </label>
        </div>
      </EditorFormPanel>

      <EditorFormPanel v-model="robotsDropdown" title="Robot settings" data-testid="open-basic-settings">
        <div class="py-2">
          <div class="flex justify-between">
            <UiFormLabel class="mb-1">Robots </UiFormLabel>
            <SfTooltip :label="robotsTooltip" :placement="'top'" :show-arrow="true" class="ml-2 z-dropdown">
              <SfIconInfo :size="'sm'" />
            </SfTooltip>
          </div>

          <Multiselect
            v-model="pageRobots"
            data-testid="page-parent"
            :options="robotNames"
            label="label"
            track-by="value"
            placeholder="Select robots"
            :allow-empty="false"
            class="cursor-pointer"
            select-label=""
            deselect-label="Selected"
          />
        </div>
      </EditorFormPanel>

      <EditorFormPanel v-model="furtherSettings" title="Further settings" data-testid="open-basic-settings">
        <div class="py-2">
          <div class="flex justify-between mb-2">
            <UiFormLabel>Canonical URL</UiFormLabel>
            <SfTooltip :label="canonicalTooltip" :placement="'top'" :show-arrow="true" class="ml-2 z-dropdown">
              <SfIconInfo :size="'sm'" />
            </SfTooltip>
          </div>
          <label>
            <SfInput v-model="canonicalLink" type="text" data-testid="seo-canonical" placeholder="Enter URL">
              <template #suffix>
                <label for="page-id" class="rounded-lg cursor-pointer">
                  <input id="page-id" v-model="data.sitemap" type="text" class="invisible w-8" />
                </label>
              </template>
            </SfInput>
          </label>
        </div>

        <div class="py-2">
          <div class="flex justify-between mb-2">
            <UiFormLabel class="mb-1">Include page in Sitemap.xml</UiFormLabel>
            <SfSwitch
              v-model="isInSitemap"
              class="checked:bg-editor-button checked:before:hover:bg-editor-button checked:border-gray-500 checked:hover:border:bg-gray-700 hover:border-gray-700 hover:before:bg-gray-700 checked:hover:bg-gray-300 checked:hover:border-gray-400"
            />
          </div>
        </div>
      </EditorFormPanel>
    </form>
  </div>

  <div v-else class="flex justify-center items-center mt-5">
    <SfLoaderCircular />
  </div>
</template>

<script setup lang="ts">
import { SfInput, SfSwitch, SfTooltip, SfIconInfo, SfLoaderCircular } from '@storefront-ui/vue';

import Multiselect from 'vue-multiselect';
import type { CategoryDetails } from '@plentymarkets/shop-api/lib/types/api/category';
const metaData = ref(false);

const { getCategoryId } = useCategoryIdHelper();

const { data, loading, fetchCategorySettings } = useCategorySettings();
const isInSitemap = computed({
  get: () => data.value.sitemap === 'Y',
  set: (val: boolean) => (data.value.sitemap = val ? 'Y' : 'N'),
});

watch(
  getCategoryId,
  async (newId: number | undefined) => {
    if (newId !== undefined) {
      await fetchCategorySettings(newId);
    }
  },
  { immediate: true },
);

const detailField = <K extends keyof CategoryDetails>(field: K) =>
  computed({
    get() {
      return data.value.details[0]?.[field] ?? '';
    },
    set(val: CategoryDetails[K]) {
      if (!data.value.details.length) {
        data.value.details.push({} as CategoryDetails);
      }
      const detail = data.value.details[0];
      if (detail) {
        detail[field] = val;
      }
    },
  });

const metaTitle = detailField('metaTitle');
const metaDescription = detailField('metaDescription');
const metaKeywords = detailField('metaKeywords');
const canonicalLink = detailField('canonicalLink');

const robotsDropdown = ref(false);
const furtherSettings = ref(false);
const robotNames = [
  { label: 'All', value: 'ALL' },
  { label: 'No Index', value: 'NOINDEX' },
  { label: 'No Follow', value: 'NOFOLLOW' },
  { label: 'No Index, No Follow', value: 'NOINDEX_NOFOLLOW' },
];

const pageRobots = computed({
  get: () => robotNames.find((robot) => robot.value === data.value.details?.[0]?.metaRobots) || robotNames[0],
  set: (val) => {
    if (!val) return;
    const detail = data.value.details[0];
    if (detail) {
      detail.metaRobots = val.value;
    }
  },
});

const titleTooltip =
  'Title displayed in search results of search engines. Enter text here to add a prefix before the shop name in the page title (shown as “your text / shop name”).';
const descTooltip =
  'Short description of the page shown in search results of search engines. Should be engaging and informative.';
const robotsTooltip =
  'This setting controls how search engines treat your pages. Choose "all" to allow indexing and following links, or select other options to restrict them.';
const keywordsTooltip =
  'List of relevant terms, separated by commas. Some search engines may use them to improve discoverability,';
const canonicalTooltip =
  'Specify a canonical URL only if it differs from this page’s own URL. Helps avoid duplicate content issues.';
</script>
